import React from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { MiddleTruncate } from "~/components/middle-truncate";
import { ProgressBar } from "~/components/progress-bar";
import { DocumentIcon } from "~/components/ui/icons/document";
import { DownloadIcon } from "~/components/ui/icons/download";
import { InformationIcon } from "~/components/ui/icons/information";
import { formatPercentage } from "~/utils/number";

const MAX_FILE_SIZE_MB = 10;

type FinancialAdjustmentFileUploadStateProps = {
  file?: File;
  fileRejections: FileRejection[];
  isDragReject: boolean;
  isDragActive: boolean;
  isUploading: boolean;
  progress: number;
};

const FinancialAdjustmentFileUploadState: React.FC<
  FinancialAdjustmentFileUploadStateProps
> = ({ file, fileRejections, isDragActive, progress }) => {
  const { i18n, t } = useTranslation();

  if (file) {
    return (
      <>
        <div className="w-5/6 space-y-4 rounded-lg border border-gray-100 bg-white p-4">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-1 truncate text-sm text-gray-900">
              <DocumentIcon className="size-4 shrink-0" />
              <MiddleTruncate
                className="max-w-[335px] text-gray-900"
                text={file.name}
              />
            </div>
            <div className="text-xs text-gray-900">
              {formatPercentage(progress / 100, i18n.language)}
            </div>
          </div>
          <ProgressBar value={progress} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="rounded-full bg-gray-50 p-3 group-data-[active=true]:bg-green-900 group-data-[active=true]:text-white">
        <DownloadIcon className="size-6 rotate-180" />
      </div>

      <div className="space-y-2 text-center">
        <div className="text-gray-900">
          {t("invoice.financial_adjustment_form.fields.file.drop")}{" "}
          <span className="cursor-pointer text-green-900">
            {t("invoice.file_upload.select_file")}
          </span>
        </div>
      </div>
      {!isDragActive && fileRejections.length > 0 ? (
        <ul className="space-y-2">
          {fileRejections
            .flatMap((rejections) => rejections.errors)
            .map((error) => (
              <li
                key={error.code}
                className="flex items-center space-x-1 text-sm font-semibold text-red-600"
              >
                <InformationIcon className="size-4" />
                {error.code === "file-invalid-type" ? (
                  <span>
                    {t("invoice.file_upload.errors.file_invalid_type")}
                  </span>
                ) : null}
                {error.code === "file-too-large" ? (
                  <span>{t("invoice.file_upload.errors.file_too_large")}</span>
                ) : null}
                {error.code === "file-too-small" ? (
                  <span>{t("invoice.file_upload.errors.file_too_small")}</span>
                ) : null}
                {error.code === "too-many-files" ? (
                  <span>{t("invoice.file_upload.errors.too_many_files")}</span>
                ) : null}
              </li>
            ))}
        </ul>
      ) : null}
    </>
  );
};

type FinancialAdjustmentFileUploadProps = {
  isUploading: boolean;
  onValueChange: (file: File) => void;
  progress: number;
  value?: File;
};

const FinancialAdjustmentFileUpload: React.FC<
  FinancialAdjustmentFileUploadProps
> = ({ onValueChange, value, isUploading, progress }) => {
  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.google-apps.spreadsheet": [".gsheet"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    maxSize: 1024 * 1024 * MAX_FILE_SIZE_MB,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length && acceptedFiles[0]) {
        onValueChange(acceptedFiles[0]);
      }
    },
  });

  return (
    <div
      className="group flex min-h-[11.5rem] cursor-pointer flex-col items-center justify-center space-y-4 rounded-lg border border-dashed border-gray-300 bg-[#FBFBFB] transition-colors hover:border-green-900 data-[active=true]:border-green-900 data-[rejected=true]:border-red-600 data-[active=true]:bg-green-300"
      data-active={isDragActive}
      data-rejected={isDragReject || fileRejections.length > 0}
      {...getRootProps()}
    >
      <input {...getInputProps()} hidden />

      <div className="flex flex-col items-center space-y-2 py-11">
        <FinancialAdjustmentFileUploadState
          file={value}
          fileRejections={fileRejections}
          isDragReject={isDragReject}
          isDragActive={isDragActive}
          isUploading={isUploading}
          progress={progress}
        />
      </div>
    </div>
  );
};

export { FinancialAdjustmentFileUpload };
